import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarCheck, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { it, enUS, fr } from "date-fns/locale";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "14:30", "15:30", "16:30", "17:30"];

const LOCALES = { it, en: enUS, fr };

export default function Prenotazioni() {
  const { t, i18n } = useTranslation();
  const locale = LOCALES[i18n.resolvedLanguage] || it;
  const services = t("services.items", { returnObjects: true });

  const [date, setDate] = useState();
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", time: "", notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const dateStr = useMemo(() => (date ? format(date, "yyyy-MM-dd") : ""), [date]);

  useEffect(() => {
    if (!dateStr) return;
    api.get(`/bookings/availability?date=${dateStr}`)
      .then((r) => setBooked(r.data.booked_times || []))
      .catch(() => setBooked([]));
  }, [dateStr]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !dateStr || !form.time) {
      toast.error(t("booking.errorRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", { ...form, date: dateStr });
      toast.success(t("booking.successMsg"));
      setForm({ name: "", email: "", phone: "", service: "", time: "", notes: "" });
      setDate(undefined);
    } catch (err) {
      toast.error(t("booking.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div data-testid="page-prenotazioni" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="label-eyebrow mb-4">{t("booking.eyebrow")}</div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#2C2F2A]">
          {t("booking.title1")} <em className="text-[#8A9A86] font-light">{t("booking.title2")}</em>.
        </h1>
        <p className="mt-6 text-lg text-[#5C6356]">{t("booking.intro")}</p>
      </div>

      <form
        onSubmit={handleSubmit}
        data-testid="booking-form"
        className="mt-12 grid lg:grid-cols-12 gap-10"
      >
        <div className="lg:col-span-7 space-y-6">
          <div className="card-soft p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <CalendarCheck className="w-5 h-5 text-[#8A9A86]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl">{t("booking.chooseDate")}</h3>
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < today || d.getDay() === 0}
              locale={locale}
              data-testid="booking-calendar"
              className="rounded-xl border border-[#E6E2D8] p-3"
            />
            <p className="text-xs text-[#5C6356] mt-3">{t("booking.closedSunday")}</p>
          </div>

          {date && (
            <div className="card-soft p-6 sm:p-8" data-testid="time-slots">
              <div className="flex items-center gap-3 mb-5">
                <Clock className="w-5 h-5 text-[#8A9A86]" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl">{t("booking.time")}</h3>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {TIME_SLOTS.map((tslot) => {
                  const isBooked = booked.includes(tslot);
                  const selected = form.time === tslot;
                  return (
                    <button
                      type="button"
                      key={tslot}
                      disabled={isBooked}
                      data-testid={`slot-${tslot}`}
                      onClick={() => setForm({ ...form, time: tslot })}
                      className={`py-3 rounded-full border text-sm transition-all ${
                        isBooked
                          ? "opacity-40 line-through cursor-not-allowed border-[#E6E2D8]"
                          : selected
                          ? "bg-[#8A9A86] text-white border-[#8A9A86]"
                          : "border-[#E6E2D8] hover:border-[#8A9A86]"
                      }`}
                    >
                      {tslot}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 space-y-5">
          <div className="card-soft p-6 sm:p-8 space-y-5">
            <h3 className="font-serif text-2xl mb-2">{t("booking.yourData")}</h3>

            <div>
              <Label htmlFor="b-service" className="label-eyebrow">{t("booking.service")} *</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger id="b-service" data-testid="booking-service" className="mt-2 rounded-full border-[#E6E2D8] h-11">
                  <SelectValue placeholder={t("booking.selectService")} />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.name} data-testid={`booking-service-option-${s.id}`}>
                      {s.name} — {s.duration}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="b-name" className="label-eyebrow">{t("booking.name")} *</Label>
              <Input
                id="b-name"
                data-testid="booking-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5"
              />
            </div>

            <div>
              <Label htmlFor="b-email" className="label-eyebrow">{t("booking.email")} *</Label>
              <Input
                id="b-email"
                type="email"
                data-testid="booking-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5"
              />
            </div>

            <div>
              <Label htmlFor="b-phone" className="label-eyebrow">{t("booking.phone")} *</Label>
              <Input
                id="b-phone"
                data-testid="booking-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5"
                placeholder="+39 ..."
              />
            </div>

            <div>
              <Label htmlFor="b-notes" className="label-eyebrow">{t("booking.notes")}</Label>
              <Textarea
                id="b-notes"
                data-testid="booking-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 rounded-2xl border-[#E6E2D8] min-h-[100px] px-5 py-3"
                placeholder={t("booking.notesPlaceholder")}
              />
            </div>

            {dateStr && form.time && (
              <div className="rounded-xl bg-[#F3F0EA] p-4 text-sm">
                <div className="label-eyebrow mb-2">{t("booking.summary")}</div>
                <div>{form.service || "—"}</div>
                <div className="text-[#5C6356]">{format(date, "EEEE d MMMM yyyy", { locale })} · {form.time}</div>
              </div>
            )}

            <button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60"
            >
              {submitting ? t("booking.submitting") : t("booking.submit")}
            </button>
            <p className="text-xs text-[#5C6356] text-center">{t("booking.footer")}</p>
          </div>
        </div>
      </form>
    </div>
  );
}
